import type { GenreId, ProviderId, SessionFilters } from '@filmsloth/shared';
import type { CatalogMeta } from '../api/client.js';
import { toggle } from '../lib/toggle.js';
import { useGenreLabel, useRating, useT } from '../i18n/index.js';

interface FilterEditorProps {
  meta: CatalogMeta;
  filters: SessionFilters;
  onChange: (filters: SessionFilters) => void;
}

/** Runtime caps offered to the host; `null` is "no limit". */
const RUNTIME_OPTIONS: (number | null)[] = [90, 105, 120, 150, 180, null];

/**
 * Release-year floors; `null` is "any year". Decades, because "nothing
 * older than about 2000" is how people actually say it.
 */
const YEAR_OPTIONS: (number | null)[] = [null, 1980, 1990, 2000, 2010, 2020];

/**
 * Minimum TMDB ratings; `null` is "any". Half steps from 6: below that
 * hardly anything is filtered, above 8 hardly anything is left.
 */
const RATING_OPTIONS: (number | null)[] = [null, 6, 6.5, 7, 7.5, 8];

/** Shared by the create screen and the lobby, so both edit filters the same way. */
export function FilterEditor({ meta, filters, onChange }: FilterEditorProps) {
  const t = useT();
  const genreLabel = useGenreLabel();
  const rating = useRating();

  return (
    <>
      <div className="field">
        <span className="field__label">{t('create.providers')}</span>
        <div className="chips">
          {meta.providers.map((provider) => {
            const selected = filters.providers.includes(provider.id as ProviderId);
            return (
              <button
                key={provider.id}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() =>
                  onChange({
                    ...filters,
                    providers: toggle(filters.providers, provider.id as ProviderId),
                  })
                }
              >
                <span className="chip__dot" style={{ background: provider.color }} />
                {provider.name}
              </button>
            );
          })}
        </div>
        <span className="muted small">{t('create.providersHint')}</span>
      </div>

      <div className="field">
        <span className="field__label">{t('create.genres')}</span>
        <div className="chips">
          {meta.genres.map((genre) => {
            const selected = filters.genres.includes(genre.id as GenreId);
            return (
              <button
                key={genre.id}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() =>
                  onChange({
                    ...filters,
                    genres: toggle(filters.genres, genre.id as GenreId),
                    // Wanting a genre takes it off the "not that" list.
                    excludedGenres: (filters.excludedGenres ?? []).filter((id) => id !== genre.id),
                  })
                }
              >
                {genreLabel(genre.id)}
              </button>
            );
          })}
        </div>
        <span className="muted small">{t('create.genresHint')}</span>
      </div>

      <div className="field">
        <span className="field__label">{t('create.excludedGenres')}</span>
        <div className="chips">
          {meta.genres.map((genre) => {
            const excluded = filters.excludedGenres ?? [];
            const selected = excluded.includes(genre.id as GenreId);
            return (
              <button
                key={genre.id}
                type="button"
                className={`chip${selected ? ' chip--selected chip--excluded' : ''}`}
                aria-pressed={selected}
                onClick={() =>
                  onChange({
                    ...filters,
                    excludedGenres: toggle(excluded, genre.id as GenreId),
                    // And ruling one out takes it off the wanted list.
                    genres: filters.genres.filter((id) => id !== genre.id),
                  })
                }
              >
                {genreLabel(genre.id)}
              </button>
            );
          })}
        </div>
        <span className="muted small">{t('create.excludedGenresHint')}</span>
      </div>

      <div className="field">
        <span className="field__label">{t('create.runtime')}</span>
        <div className="chips">
          {RUNTIME_OPTIONS.map((option) => {
            const selected = filters.maxRuntimeMinutes === option;
            return (
              <button
                key={option ?? 'none'}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onChange({ ...filters, maxRuntimeMinutes: option })}
              >
                {option === null ? t('common.noLimit') : t('common.minutes', { count: option })}
              </button>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span className="field__label">{t('create.minYear')}</span>
        <div className="chips">
          {YEAR_OPTIONS.map((option) => {
            const selected = (filters.minYear ?? null) === option;
            return (
              <button
                key={option ?? 'any'}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onChange({ ...filters, minYear: option })}
              >
                {option === null ? t('common.anyYear') : t('common.fromYear', { year: option })}
              </button>
            );
          })}
        </div>
      </div>

      <div className="field">
        <span className="field__label">{t('create.minRating')}</span>
        <div className="chips">
          {RATING_OPTIONS.map((option) => {
            const selected = (filters.minRating ?? null) === option;
            return (
              <button
                key={option ?? 'any'}
                type="button"
                className={`chip${selected ? ' chip--selected' : ''}`}
                aria-pressed={selected}
                onClick={() => onChange({ ...filters, minRating: option })}
              >
                {option === null
                  ? t('common.anyRating')
                  : t('common.fromRating', { rating: rating(option) })}
              </button>
            );
          })}
        </div>
        <span className="muted small">{t('create.minRatingHint')}</span>
      </div>
    </>
  );
}
